import { useEffect, useMemo, useRef } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { useAutoZoomToHomeAor } from '../AorsTab/utils/useAutoZoomToHomeAor';
import { useAors } from 'hooks/useAors';
import { SitStatLayerListShell } from './SitStatLayerListShell';
import { useAorText } from 'utils/aorTerminology';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { scopeIncludesAor } from './sitstat-aor-scope';

export function SitStatAorsWidget() {
  const { data: allAors = [] } = useAors();
  const { aorFilterIds, setAorFilterIds, isAorVisible, setIsAorVisible } =
    useUscgMapContext();
  const { zoomToAorById } = useAutoZoomToHomeAor();
  const { scope, selectedAorId } = useSitStatAorScope();
  const aorText = useAorText();
  const enabledDefaultRef = useRef(false);

  // The scope provider owns the map filter while an AOR is selected.
  useEffect(() => {
    if (enabledDefaultRef.current) return;
    enabledDefaultRef.current = true;
    setIsAorVisible(true);
    if (!selectedAorId) setAorFilterIds(null);
  }, [selectedAorId, setIsAorVisible, setAorFilterIds]);

  const aors = useMemo(
    () => allAors.filter((aor) => scopeIncludesAor(scope, String(aor.id))),
    [allAors, scope]
  );
  const allAorIds = aors.map((aor) => String(aor.id));
  // Unscoped, "every AOR" is the `null` filter; scoped, it is the scope's ids.
  const allFilter = scope ? allAorIds : null;
  const currentIds =
    aorFilterIds === null
      ? allAorIds
      : aorFilterIds.filter((id) => allAorIds.includes(id));
  const visibleCount = isAorVisible ? currentIds.length : 0;
  const isAllVisible =
    isAorVisible && aors.length > 0 && visibleCount === aors.length;
  const someVisible = visibleCount > 0 && visibleCount < aors.length;

  const isAorOnMap = (id: string) =>
    isAorVisible && (aorFilterIds === null || aorFilterIds.includes(id));

  const toggleOne = (id: string, visible: boolean) => {
    if (!isAorVisible) {
      setIsAorVisible(true);
    }
    if (visible) {
      if (aorFilterIds === null) return;
      const next = [...new Set([...currentIds, id])];
      setAorFilterIds(next.length === allAorIds.length ? allFilter : next);
      return;
    }

    setAorFilterIds(currentIds.filter((entry) => entry !== id));
  };

  const toggleAll = (visible: boolean) => {
    if (visible) {
      setIsAorVisible(true);
      setAorFilterIds(allFilter);
      return;
    }
    setAorFilterIds([]);
    setIsAorVisible(false);
  };

  const focusAor = (id: string) => {
    void zoomToAorById(id, { filterIds: [id], ensureVisible: true });
  };

  const zoomAll = () => {
    const first = allAorIds[0];
    if (!first) return;
    void zoomToAorById(first, {
      filterIds: allAorIds,
      ensureVisible: true,
    });
  };

  return (
    <SitStatLayerListShell
      title={aorText('AORs')}
      checked={isAllVisible}
      indeterminate={someVisible}
      onCheckedChange={toggleAll}
      defaultOpen
      className="tw:w-full"
      contentClassName="tw:px-1.5 tw:py-2"
      onZoom={zoomAll}
      zoomDisabled={aors.length === 0}
      zoomLabel={aorText('Zoom to all AORs')}
    >
      <ul className="list-unstyled tw:m-0 tw:w-full tw:list-none tw:!space-y-1.5 tw:!p-0 tw:!pl-0">
        {aors.map((aor) => {
          const id = String(aor.id);
          const onMap = isAorOnMap(id);
          return (
            <li
              key={id}
              className="tw:flex tw:w-full tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-border/60 tw:px-2 tw:py-1.5"
            >
              <Checkbox
                checked={onMap}
                onCheckedChange={(value) => {
                  toggleOne(id, value === true);
                }}
                aria-label={`Toggle ${aor.name} on map`}
                className="tw:shrink-0"
              />
              <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-xs tw:font-medium">
                {aor.name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Zoom to ${aor.name}`}
                title="Zoom to map location"
                className="tw:shrink-0"
                onClick={() => {
                  focusAor(id);
                }}
              >
                <MapIcon className="tw:h-3.5 tw:w-3.5" />
              </Button>
            </li>
          );
        })}
      </ul>
    </SitStatLayerListShell>
  );
}
